import { useEffect, useRef } from 'react';
import { Button } from '../components/Button.jsx';
import { Card } from '../components/Card.jsx';
import { scrollToTop, useFocusOnMount } from '../components/useFocusOnMount.js';
import { revealCop30, finaliseGame } from '../state/actions.js';
import { CARBON_TAX_BRACKETS } from '../data/brackets.js';
import { COP30_OUTCOME_ID } from '../data/cop30.js';
import { lookupCarbonBracket, pickCarbonMultiplier } from '../engine/carbonTax.js';
import { formatEmissions, formatMoney } from '../components/format.js';
import { useAudio } from '../audio/AudioProvider.jsx';

function bracketTone(base) {
  if (base > 0) return 'clean';
  if (base < 0) return 'carbon';
  return 'neutral';
}

function deltaTone(delta) {
  if (delta > 0) return 'clean';
  if (delta < 0) return 'carbon';
  return 'neutral';
}

function bracketNarrative(value) {
  if (value === 0) return 'Neutral';
  if (value > 0) return `${formatMoney(value, { sign: true })} bonus`;
  return `${formatMoney(value)} penalty`;
}

function formatDeltaParens(delta) {
  if (delta === 0) return '';
  const rounded = Math.round(delta * 10) / 10;
  const abs = Math.abs(rounded);
  const body = rounded % 1 === 0 ? abs.toFixed(0) : abs.toFixed(1);
  return rounded > 0 ? `(+${body})` : `(-${body})`;
}

const COP_OUTCOME_IMAGES = {
  [COP30_OUTCOME_ID.AMBITIOUS]: {
    src: '/COP-Sucess.webp',
    alt: 'Successful COP 31 Antalya climate agreement'
  },
  [COP30_OUTCOME_ID.MODEST]: {
    src: '/Cop_neutral.webp',
    alt: 'Neutral COP 31 Antalya climate agreement'
  },
  [COP30_OUTCOME_ID.COLLAPSE]: {
    src: '/COP_collapse.webp',
    alt: 'Collapsed COP 31 Antalya climate talks'
  }
};

const COP_POLICY_SIGNALS = {
  [COP30_OUTCOME_ID.COLLAPSE]: {
    cleanSignal: 'Support weakened',
    carbonSignal: 'Enforcement softened',
    summary:
      'A fractured summit reduces clean-energy support, but also weakens pressure on high-emission portfolios.'
  },
  [COP30_OUTCOME_ID.MODEST]: {
    cleanSignal: 'Support steady',
    carbonSignal: 'Rules unchanged',
    summary:
      'The existing carbon framework holds. No extra support, no extra pressure.'
  },
  [COP30_OUTCOME_ID.AMBITIOUS]: {
    cleanSignal: 'Support accelerated',
    carbonSignal: 'Enforcement tightened',
    summary:
      'A stronger accord boosts clean-energy rewards and increases pressure on high-emission portfolios.'
  }
};

const COP_OUTCOME_CUES = {
  [COP30_OUTCOME_ID.AMBITIOUS]: 'cop31AmbitiousTargets',
  [COP30_OUTCOME_ID.MODEST]: 'cop31ModestAgreement',
  [COP30_OUTCOME_ID.COLLAPSE]: 'cop31SummitCollapses'
};

export function Cop30Screen({
  state,
  dispatch,
  onReveal,
  outcomeAudioEnabled = true
}) {
  const mainRef = useFocusOnMount();
  const revealed = state.cop30 !== null;
  const { playCue } = useAudio();
  const outcomeCuePlayedRef = useRef(false);

  useEffect(() => {
    scrollToTop();
  }, [revealed]);

  useEffect(() => {
    if (
      !outcomeAudioEnabled ||
      !revealed ||
      !state.cop30 ||
      outcomeCuePlayedRef.current
    ) {
      return undefined;
    }

    const cueName = COP_OUTCOME_CUES[state.cop30.id];
    if (!cueName) return undefined;

    // Fires as soon as the outcome is on screen. The COP transition
    // animation already gates this via outcomeAudioEnabled
    // (GameView.jsx), so an extra delay here only made the verdict
    // land late.
    outcomeCuePlayedRef.current = true;
    playCue(cueName);
    return undefined;
  }, [outcomeAudioEnabled, playCue, revealed, state.cop30]);

  const cumulativeEmissions = state.yearResults.reduce(
    (sum, r) => sum + r.emissions,
    0
  );
  const currentBracket = lookupCarbonBracket(
    cumulativeEmissions,
    CARBON_TAX_BRACKETS
  );
  const emissionsTone = bracketTone(currentBracket.base);
  const currentMultiplier = revealed
    ? pickCarbonMultiplier(currentBracket, state.cop30)
    : 1;
  const currentPost = currentBracket.base * currentMultiplier;
  const currentDelta = currentPost - currentBracket.base;
  const headerNarrative = revealed
    ? bracketNarrative(currentPost)
    : currentBracket.narrative;
  const outcomeImage = revealed ? COP_OUTCOME_IMAGES[state.cop30.id] : null;
  const policySignals = revealed ? COP_POLICY_SIGNALS[state.cop30.id] : null;

  function handleReveal() {
    playCue('cop31Convenes');
    if (onReveal) {
      onReveal();
    } else {
      dispatch(revealCop30());
    }
  }

  const outcomeCard = (
    <Card title="COP 31 Antalya outcome" titleId="cop30-outcome-heading">
      {revealed ? (
        <>
          <p className="card-body">
            <strong>{state.cop30.headline}.</strong> {state.cop30.narrative}
          </p>
          {outcomeImage ? (
            <img
              className="cop30-outcome-image"
              src={outcomeImage.src}
              alt={outcomeImage.alt}
              loading="eager"
              decoding="async"
            />
          ) : null}
          <dl className="cop30-stats">
            <div>
              <dt>Clean transition signal</dt>
              <dd>{policySignals.cleanSignal}</dd>
            </div>
            <div>
              <dt>Carbon enforcement signal</dt>
              <dd>{policySignals.carbonSignal}</dd>
            </div>
          </dl>
          <p className="card-body">
            {policySignals.summary} Tally the final score to see your result.
          </p>
          <div className="screen-actions">
            <Button
              onClick={() => dispatch(finaliseGame())}
              changesScreen
              testId="cop30-finalise"
            >
              Tally final score
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="card-body">
            The world has been negotiating in parallel with your build.
            The COP 31 Antalya outcome decides whether your bracket is punished,
            held neutral, or rewarded at the final tally.
          </p>
          <div className="screen-actions">
            <Button onClick={handleReveal} testId="cop30-reveal" audioCue="none">
              Reveal COP 31 Antalya outcome
            </Button>
          </div>
        </>
      )}
    </Card>
  );

  const bracketTable = (
    <section
      className="cop30-bracket-table-wrap"
      aria-labelledby="cop30-bracket-heading"
    >
      <div className="cop30-section-head">
        <h3 id="cop30-bracket-heading" className="cop30-section-heading">
          Carbon tax brackets
        </h3>
        <p className="cop30-section-note">
          Base outcome before COP 31 Antalya multipliers. Lower emissions earn
          bonuses; higher emissions are penalised.
        </p>
      </div>
      <table className="cop30-bracket-table">
        <thead>
          <tr>
            <th scope="col">Emissions (tCO2e)</th>
            <th scope="col">Base outcome</th>
          </tr>
        </thead>
        <tbody>
          {CARBON_TAX_BRACKETS.map((bracket) => {
            const isCurrent = bracket.label === currentBracket.label;
            const tone = bracketTone(bracket.base);

            return (
              <tr
                key={bracket.label}
                className={
                  `cop30-bracket-row cop30-bracket-row-${tone}` +
                  (isCurrent ? ' cop30-bracket-row-current' : '')
                }
                data-testid={`cop30-bracket-row-${bracket.label}`}
                aria-current={isCurrent ? 'true' : undefined}
              >
                <td className="cop30-bracket-cell-range">
                  <span
                    className={
                      'cop30-bracket-marker' +
                      (isCurrent ? '' : ' cop30-bracket-marker-empty')
                    }
                    aria-hidden="true"
                  >
                    &#9654;
                  </span>
                  <span className="cop30-bracket-range">{bracket.label}</span>
                  {isCurrent ? (
                    <span className="cop30-bracket-you">You are here</span>
                  ) : null}
                </td>
                <td className="cop30-bracket-narrative">
                  <span className="cop30-bracket-narrative-text">
                    {bracket.narrative}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );

  return (
    <main ref={mainRef} id="main" tabIndex={-1} className="screen screen-cop30">
      <header className="cop30-header">
        <div className="cop30-eyebrow">Three-year carbon ledger</div>
        <h2 className="cop30-title" id="cop30-heading">
          <span className="cop30-title-label">Total emissions</span>
          <span
            className={`cop30-title-value cop30-title-value-${emissionsTone}`}
          >
            {formatEmissions(cumulativeEmissions)}
            <span className="cop30-title-unit">tCO2e</span>
          </span>
        </h2>
        <div className={`cop30-band-pill cop30-band-pill-${emissionsTone}`}>
          <span className="cop30-band-pill-label">Your band</span>
          <span className="cop30-band-pill-range">{currentBracket.label}</span>
          <span className="cop30-band-pill-narrative">
            {headerNarrative}
            {revealed && currentDelta !== 0 ? (
              <span
                className={`cop30-band-pill-delta cop30-band-pill-delta-${deltaTone(currentDelta)}`}
                data-testid="cop30-band-pill-delta"
              >
                {' '}
                {formatDeltaParens(currentDelta)}
              </span>
            ) : null}
          </span>
        </div>
      </header>

      {revealed ? null : bracketTable}
      {outcomeCard}
    </main>
  );
}
